/**
 * 都市商务模板 - 注册页
 *
 * 设计核心理念：顶部深色横幅 + 中央悬浮表单卡
 * 与 sunset（左右分栏优势区 + 表单）完全不同的排版骨架：
 * - 顶部：深藏青渐变横幅（站点名 + 标题 + 标语）
 * - 中央：白色悬浮表单卡片（带阴影浮在横幅上）
 * - 底部：优势要点横排
 *
 * 字段逻辑（动态渲染 + 短信验证 + 协议弹窗）复用 src/pages/register/page.tsx 的实现，
 * 必须遵循 TEMPLATE.md 4.7 节规范：
 * - GET /register/config?site_id= 获取字段，只渲染 is_enabled 的字段
 * - sms_enabled 为 true 时渲染手机号 + 验证码字段
 * - 提交直调 request('/auth/register')，不用 useAuth().register
 * - field_type 为 file/image 的先上传拿 URL 再提交
 * - 成功跳转首页；协议勾选 + 协议弹窗（getSinglePage）
 */

import { useState, useEffect, useCallback, useRef, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";
import request, { getSinglePage } from "@/utils/api";

// 注册字段类型
type FieldType = "text" | "textarea" | "select" | "radio" | "checkbox" | "password" | "number" | "date" | "file" | "image";

interface RegisterField {
  field_name: string;
  field_label: string;
  field_type: FieldType;
  placeholder: string;
  hint?: string;
  options?: string[];
  is_required: boolean;
  is_enabled: boolean;
  sort_order: number;
}

// 获取当前站点ID
const getSiteId = () => localStorage.getItem('current_site_id') || '';

// 获取注册配置
async function getRegisterConfig(siteId: string): Promise<{ fields: RegisterField[]; sms_enabled?: boolean }> {
  return request<{ fields: RegisterField[]; sms_enabled?: boolean }>(`/register/config`, {
    params: { site_id: siteId },
  });
}

// 注册参数接口
interface RegisterParams {
  username: string;
  password: string;
  site_id: string;
  confirm_password?: string;
  phone?: string;
  sms_code?: string;
  [key: string]: string | undefined;
}

// 注册函数（直调 /auth/register，不用 useAuth().register）
async function registerApi(params: RegisterParams) {
  return request("/auth/register", {
    method: "POST",
    body: JSON.stringify(params),
  });
}

// 发送短信验证码
async function sendSmsCode(siteId: string, phone: string): Promise<void> {
  return request("/register/send-code", {
    method: "POST",
    params: { site_id: siteId },
    body: { phone },
  });
}

// 文件上传函数（file/image 字段先上传拿 URL 再提交）
async function uploadFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  // 注意：不要手动设置 Content-Type，浏览器会自动添加正确的 multipart/form-data 及 boundary
  return request("/upload", {
    method: "POST",
    body: formData,
  });
}

export default function UrbanRegisterPage() {
  const navigate = useNavigate();
  const { siteConfig } = useSiteConfig();

  const [fields, setFields] = useState<RegisterField[]>([]);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [uploadingFields, setUploadingFields] = useState<Record<string, boolean>>({});
  const formDataRef = useRef<Record<string, string>>({});

  // 短信验证相关
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);

  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";

  // 协议弹窗相关
  const [agreementModal, setAgreementModal] = useState<{ show: boolean; title: string; content: string; loading: boolean }>({
    show: false, title: "", content: "", loading: false,
  });

  // 打开协议弹窗并加载内容
  const openAgreement = useCallback(async (slug: string, title: string) => {
    setAgreementModal({ show: true, title, content: "", loading: true });
    try {
      const page = await getSinglePage(slug);
      setAgreementModal({
        show: true,
        title: page?.title || title,
        content: page?.content || "暂无内容",
        loading: false,
      });
    } catch {
      setAgreementModal({ show: true, title, content: "加载失败，请稍后重试", loading: false });
    }
  }, []);

  const closeAgreement = useCallback(() => {
    setAgreementModal({ show: false, title: "", content: "", loading: false });
  }, []);

  // 加载注册配置（只渲染 is_enabled 的字段）
  useEffect(() => {
    async function loadConfig() {
      try {
        const config = await getRegisterConfig(getSiteId());
        const enabledFields = config.fields.filter((f) => f.is_enabled);

        const initialData: Record<string, string> = {};
        enabledFields.forEach((field) => {
          initialData[field.field_name] = "";
        });

        // 设置短信验证开关
        if (config.sms_enabled) {
          initialData['phone'] = '';
          initialData['sms_code'] = '';
          setSmsEnabled(true);
        }

        setFields(enabledFields);
        setFormData(initialData);
        formDataRef.current = initialData;
      } catch (err) {
        console.error("加载注册配置失败:", err);
        setError("加载表单配置失败，请刷新重试");
      } finally {
        setIsLoadingConfig(false);
      }
    }
    loadConfig();
  }, []);

  // 短信倒计时
  useEffect(() => {
    if (smsCountdown > 0) {
      const timer = setTimeout(() => setSmsCountdown(smsCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [smsCountdown]);

  // 发送短信验证码
  const handleSendSmsCode = useCallback(async () => {
    const phone = formDataRef.current['phone'] || '';

    if (!phone) {
      setError("请先输入手机号");
      return;
    }

    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError("手机号格式不正确");
      return;
    }

    try {
      setSmsLoading(true);
      await sendSmsCode(getSiteId(), phone);
      setSmsCountdown(60);
      setError("");
    } catch (err: any) {
      setError(err.message || "短信发送失败，请重试");
    } finally {
      setSmsLoading(false);
    }
  }, []);

  // 处理输入变化
  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: value };
      formDataRef.current = newData;
      return newData;
    });
  }, []);

  // 切换密码显示
  const togglePasswordVisibility = useCallback((fieldName: string) => {
    setShowPasswords((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  }, []);

  // 处理文件上传（先上传拿 URL 再写入字段值）
  const handleFileUpload = useCallback(async (fieldName: string, file: File) => {
    try {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
      const result = await uploadFile(file);
      handleChange(fieldName, result.url);
    } catch (err) {
      console.error("文件上传失败:", err);
      alert("文件上传失败，请重试");
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  }, [handleChange]);

  // 获取图标
  const getFieldIcon = (fieldType: FieldType) => {
    switch (fieldType) {
      case "text":
      case "number":
        return "ri-edit-line";
      case "textarea":
        return "ri-file-text-line";
      case "select":
      case "radio":
      case "checkbox":
        return "ri-list-check";
      case "password":
        return "ri-lock-line";
      case "date":
        return "ri-calendar-line";
      case "file":
      case "image":
        return "ri-image-add-line";
      default:
        return "ri-edit-line";
    }
  };

  // urban 统一输入框样式
  const baseInputClass = "w-full pl-10 pr-4 py-3 bg-[#F4F6F8] border border-transparent rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1B3A5C]/30 focus:ring-4 focus:ring-[#1B3A5C]/5 transition-all";

  // 渲染字段
  const renderField = (field: RegisterField) => {
    const value = formDataRef.current[field.field_name] || "";
    const isPassword = field.field_type === "password";
    const showPassword = showPasswords[field.field_name] || false;

    switch (field.field_type) {
      case "textarea":
        return (
          <textarea
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            rows={3}
            className="w-full px-4 py-3 bg-[#F4F6F8] border border-transparent rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1B3A5C]/30 focus:ring-4 focus:ring-[#1B3A5C]/5 transition-all resize-none"
          />
        );

      case "select":
        return (
          <select
            key={field.field_name}
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          >
            <option value="">{field.placeholder || `请选择${field.field_label}`}</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                  value === option
                    ? "border-[#F08A24]/30 bg-orange-50"
                    : "border-gray-200 bg-[#F4F6F8]"
                }`}
              >
                <input
                  type="radio"
                  name={field.field_name}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleChange(field.field_name, e.target.value)}
                  className="w-4 h-4 text-[#F08A24]"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div key={field.field_name} className="space-y-2">
            {field.options?.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 bg-[#F4F6F8] cursor-pointer"
              >
                <input
                  type="checkbox"
                  name={field.field_name}
                  value={option}
                  checked={value.split(",").includes(option)}
                  onChange={(e) => {
                    const currentValues = value ? value.split(",") : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    handleChange(field.field_name, newValues.join(","));
                  }}
                  className="w-4 h-4 text-[#F08A24] rounded"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "date":
        return (
          <input
            key={field.field_name}
            type="date"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            className={baseInputClass}
          />
        );

      case "number":
        return (
          <input
            key={field.field_name}
            type="number"
            value={value}
            onChange={(e) => handleChange(field.field_name, e.target.value)}
            placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
            className={baseInputClass}
          />
        );

      case "file":
      case "image":
        return (
          <div key={field.field_name}>
            <label
              htmlFor={`file-${field.field_name}`}
              className="flex flex-col items-center justify-center w-full px-4 py-6 bg-[#F4F6F8] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-orange-50 hover:border-[#F08A24]/40 transition-colors"
            >
              {uploadingFields[field.field_name] ? (
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 border-4 border-[#F08A24] border-t-transparent rounded-full animate-spin mb-2"></div>
                  <span className="text-sm text-gray-500">上传中...</span>
                </div>
              ) : value ? (
                <div className="flex flex-col items-center">
                  {field.field_type === "image" ? (
                    <img
                      src={value}
                      alt="预览"
                      className="w-24 h-24 object-cover rounded-lg mb-2"
                      onError={(e) => {
                        console.error('图片加载失败:', value);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <i className="ri-file-check-line text-3xl text-green-500 mb-2"></i>
                  )}
                  <span className="text-sm text-gray-600">点击重新上传</span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <i className="ri-upload-cloud-2-line text-3xl text-gray-400 mb-2"></i>
                  <span className="text-sm text-gray-500">{field.placeholder || `点击上传${field.field_type === "image" ? "图片" : "文件"}`}</span>
                  {field.hint && <span className="text-xs text-gray-400 mt-1">{field.hint}</span>}
                </div>
              )}
            </label>
            <input
              id={`file-${field.field_name}`}
              type="file"
              accept={field.field_type === "image" ? "image/*" : "*/*"}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileUpload(field.field_name, file);
                }
              }}
              className="hidden"
            />
            {value && (
              <button
                type="button"
                onClick={() => handleChange(field.field_name, "")}
                className="mt-2 text-sm text-red-500 hover:text-red-600"
              >
                清除
              </button>
            )}
          </div>
        );

      default:
        // text 或 password
        return (
          <div className="relative">
            <input
              key={field.field_name}
              type={isPassword ? (showPassword ? "text" : "password") : "text"}
              value={value}
              onChange={(e) => handleChange(field.field_name, e.target.value)}
              placeholder={field.placeholder || field.hint || `请输入${field.field_label}`}
              className={isPassword ? `${baseInputClass} pr-11` : baseInputClass}
            />
            {isPassword && (
              <button
                type="button"
                onClick={() => togglePasswordVisibility(field.field_name)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className={showPassword ? "ri-eye-line" : "ri-eye-off-line"} />
              </button>
            )}
          </div>
        );
    }
  };

  // 表单验证
  const validate = () => {
    const currentData = formDataRef.current;

    if (!agreed) {
      setError("请先阅读并同意注册协议和隐私政策");
      return false;
    }

    // 检查必填字段
    for (const field of fields) {
      if (field.is_required && !(currentData[field.field_name] || "").trim()) {
        setError(`${field.field_label}不能为空`);
        return false;
      }
    }

    // 短信验证（如果启用）
    if (smsEnabled) {
      const phone = currentData['phone'] || '';
      const smsCode = currentData['sms_code'] || '';

      if (!phone) {
        setError("请输入手机号");
        return false;
      }
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        setError("手机号格式不正确");
        return false;
      }
      if (!smsCode) {
        setError("请输入短信验证码");
        return false;
      }
      if (smsCode.length !== 6) {
        setError("短信验证码为6位数字");
        return false;
      }
    }

    // 验证用户名格式
    const usernameField = fields.find((f) => f.field_name === "username");
    if (usernameField && currentData.username) {
      if (!/^\w{3,20}$/.test(currentData.username)) {
        setError("用户名只能包含字母、数字和下划线，长度3-20位");
        return false;
      }
    }

    // 验证密码长度
    if (currentData.password && currentData.password.length < 6) {
      setError("密码长度至少6位");
      return false;
    }

    return true;
  };

  // 提交表单（直调 /auth/register，携带自定义字段）
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (!validate()) return;

    setIsLoading(true);
    try {
      const currentData = formDataRef.current;

      const params: RegisterParams = {
        username: currentData.username,
        password: currentData.password,
        site_id: getSiteId(),
        confirm_password: currentData.confirm_password || '',
      };

      // 添加自定义字段（排除已处理的字段和 nickname）
      fields.forEach((field) => {
        if (!["username", "password", "nickname", "confirm_password"].includes(field.field_name)) {
          params[field.field_name] = currentData[field.field_name] ?? '';
        }
      });

      // 添加短信验证码参数（如果启用）
      if (smsEnabled) {
        params.phone = currentData['phone'];
        params.sms_code = currentData['sms_code'];
      }

      await registerApi(params);

      // 注册成功，跳转首页
      navigate("/");
    } catch (err: any) {
      setError(err.message || "注册失败，请稍后重试");
    } finally {
      setIsLoading(false);
    }
  };

  const advantages = [
    { icon: "ri-file-list-3-line", title: "快速发布", desc: "分类信息一键发布" },
    { icon: "ri-eye-line", title: "流量曝光", desc: "覆盖本地用户" },
    { icon: "ri-vip-crown-line", title: "VIP特权", desc: "置顶优先展示" },
    { icon: "ri-shield-check-line", title: "安全可靠", desc: "信息审核保障" },
  ];

  // 加载中
  if (isLoadingConfig) {
    return (
      <div className="min-h-screen bg-[#F4F6F8] flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-[#F08A24]" />
          <p className="mt-2 text-gray-500 text-sm">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6F8] flex flex-col">
      {/* ── 顶部深色横幅 ── */}
      <div className="bg-gradient-to-br from-[#16283F] via-[#1B3A5C] to-[#2C5A8C] relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#F08A24]/10 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-white/5 blur-3xl" />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center relative z-10">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-6">
            {logo ? (
              <img src={logo} alt={siteName} className="h-10 w-auto" />
            ) : (
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-[#F08A24] to-[#E26A1B] flex items-center justify-center text-white text-xl font-black shadow-lg shadow-orange-500/30">
                {siteName ? siteName.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
            {siteName && <span className="text-xl font-bold text-white">{siteName}</span>}
          </div>

          <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">
            加入我们，开启
            <span className="block mt-1 bg-gradient-to-r from-[#F08A24] to-[#F5A94D] bg-clip-text text-transparent">
              分类信息之旅
            </span>
          </h1>
          <p className="text-gray-300 mt-3 text-sm">注册账户，开启您的分类信息之旅</p>

          {/* 优势横排 */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {advantages.map((item, idx) => (
              <div key={idx} className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-[#F5A94D] text-lg`} />
                </div>
                <div className="text-center">
                  <div className="text-xs font-semibold text-white">{item.title}</div>
                  <div className="text-[10px] text-gray-400">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── 中央悬浮表单卡 ── */}
      <div className="flex-1 flex justify-center">
        <div className="w-full max-w-lg px-4 sm:px-6 -mt-8 pb-14">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-xl shadow-[#1B3A5C]/10 p-7 sm:p-9">
            <div className="flex items-center justify-between mb-7">
              <h2 className="text-xl font-bold text-gray-800">创建账户</h2>
              <Link to="/login" className="text-sm text-[#1B3A5C] hover:text-[#F08A24] font-semibold transition-colors">
                已有账号？登录
              </Link>
            </div>

            {error && (
              <div className="mb-5 p-3.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line" />{error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 动态渲染表单字段（后台配置，只渲染 is_enabled 的字段） */}
              {fields.map((field) => (
                <div key={field.field_name}>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    {field.field_label}
                    {field.is_required && <span className="text-red-500">*</span>}
                  </label>
                  <div className="relative">
                    <i
                      className={`${getFieldIcon(field.field_type)} absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400`}
                    />
                    {renderField(field)}
                  </div>
                  {field.hint && (
                    <p className="mt-1 text-xs text-gray-400">{field.hint}</p>
                  )}
                </div>
              ))}

              {/* 短信验证码（如果启用） */}
              {smsEnabled && (
                <>
                  {/* 手机号 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      手机号 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="ri-phone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="tel"
                        value={formData['phone'] || ''}
                        onChange={(e) => {
                          handleChange('phone', e.target.value);
                          setError("");
                        }}
                        placeholder="请输入手机号"
                        className="w-full pl-10 pr-24 py-3 bg-[#F4F6F8] border border-transparent rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1B3A5C]/30 focus:ring-4 focus:ring-[#1B3A5C]/5 transition-all"
                      />
                      <button
                        type="button"
                        onClick={handleSendSmsCode}
                        disabled={smsCountdown > 0 || smsLoading}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] disabled:from-gray-300 disabled:to-gray-300 disabled:text-gray-500 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {smsLoading ? '发送中...' : smsCountdown > 0 ? `${smsCountdown}s后重发` : '获取验证码'}
                      </button>
                    </div>
                  </div>

                  {/* 验证码 */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">
                      短信验证码 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <i className="ri-shield-check-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={formData['sms_code'] || ''}
                        onChange={(e) => {
                          // 只允许数字
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                          handleChange('sms_code', value);
                          setError("");
                        }}
                        placeholder="请输入6位验证码"
                        maxLength={6}
                        className="w-full pl-10 pr-4 py-3 bg-[#F4F6F8] border border-transparent rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:bg-white focus:border-[#1B3A5C]/30 focus:ring-4 focus:ring-[#1B3A5C]/5 transition-all tracking-widest"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* 协议勾选 */}
              <div className="pt-1">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="w-4 h-4 rounded accent-[#1B3A5C] mt-0.5 shrink-0"
                  />
                  <span className="text-xs text-gray-500 leading-relaxed">
                    我已阅读并同意
                    <span
                      className="text-[#1B3A5C] hover:text-[#F08A24] mx-1 cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); openAgreement("register-agreement", "注册协议"); }}
                    >
                      《注册协议》
                    </span>
                    和
                    <span
                      className="text-[#1B3A5C] hover:text-[#F08A24] ml-1 cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); openAgreement("privacy", "隐私政策"); }}
                    >
                      《隐私政策》
                    </span>
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] transition-all shadow-lg shadow-orange-500/25 disabled:opacity-60 disabled:cursor-not-allowed text-sm mt-2"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />注册中...
                  </span>
                ) : "创建账户"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/" className="text-sm text-gray-400 hover:text-gray-500 inline-flex items-center gap-1 transition-colors">
                <i className="ri-arrow-left-line" />返回首页
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── 协议弹窗 ── */}
      {agreementModal.show && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={closeAgreement}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-file-text-line text-[#F08A24]" />
                {agreementModal.title}
              </h2>
              <button
                onClick={closeAgreement}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            {/* 内容 */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {agreementModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <i className="ri-loader-4-line animate-spin text-2xl text-[#F08A24]" />
                    <p className="mt-2 text-sm text-gray-400">加载中...</p>
                  </div>
                </div>
              ) : (
                <div
                  className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: agreementModal.content }}
                />
              )}
            </div>
            {/* 底部按钮 */}
            <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end">
              <button
                onClick={closeAgreement}
                className="px-6 py-2.5 bg-gradient-to-r from-[#F08A24] to-[#E26A1B] text-white font-bold rounded-lg hover:from-[#E26A1B] hover:to-[#C95A12] transition-colors text-sm cursor-pointer"
              >
                已了解，关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
